// ISO 4217 currency minor-unit precision + conversion helpers for the widget.
//
// CHG-3220 / ITER-905957. Mirror of the backend canonical table at
// src/api/app/core/currency.py (CURRENCY_PRECISION / currency_precision /
// minor_units_to_major / major_to_minor_units), which itself mirrors
// specs/seeds/billing-management/currency-precision.yaml.
//
// The widget checkout previously hardcoded a fixed `* 100` / `/ 100`
// major<->minor conversion for ALL currencies, which 100x-overcharged
// zero-decimal currencies (JPY/KRW) and 10x-undercharged three-decimal
// currencies (BHD/KWD). These helpers restore per-currency precision so the
// wire `amount_minor_units` matches the backend's interpretation.
//
// Keep this table byte-identical (modulo syntax) to the backend override set;
// if the two drift, zero/three-decimal orgs are mischarged. There is no
// runtime cross-check on the client, so this file is the contract.

// Minor-unit precision overrides. Default is 2 decimals for any currency not
// listed (USD, EUR, GBP, BDT, INR, etc.). HUF + IDR are treated as
// zero-decimal by industry/card-payment convention (matches the backend; see
// core/currency.py docstring), even though ISO 4217 lists them as 2-decimal.
export const CURRENCY_PRECISION: Record<string, number> = {
  // Zero-decimal — 1 minor unit = 1 major unit (e.g. ¥100 stored as 100).
  BIF: 0,
  CLP: 0,
  DJF: 0,
  GNF: 0,
  HUF: 0, // industry convention; ISO 4217 says 2 (see backend docstring)
  IDR: 0, // industry convention; ISO 4217 says 2
  ISK: 0,
  JPY: 0,
  KMF: 0,
  KRW: 0,
  PYG: 0,
  RWF: 0,
  UGX: 0,
  VND: 0,
  VUV: 0,
  XAF: 0,
  XOF: 0,
  XPF: 0,
  // Three-decimal — 1 minor unit = 0.001 major unit (e.g. BD 1.250 = 1250).
  BHD: 3,
  IQD: 3,
  JOD: 3,
  KWD: 3,
  LYD: 3,
  OMR: 3,
  TND: 3,
};

/** Decimal places for an ISO 4217 currency code. Default 2 for unlisted. */
export function currencyExponent(code: string | null | undefined): number {
  if (!code) return 2;
  return CURRENCY_PRECISION[code.toUpperCase()] ?? 2;
}

/**
 * Convert a major-unit amount (donor-entered) to the integer minor-unit wire
 * amount for the given currency. Mirrors backend major_to_minor_units, but
 * keeps the widget's pre-existing half-up Math.round (the backend uses
 * banker's rounding; the widget has always used Math.round and donor inputs
 * are whole/2-decimal, so the two never disagree in practice).
 *
 *   majorToMinorUnits(25,   'USD') -> 2500
 *   majorToMinorUnits(2500, 'JPY') -> 2500   (was 250000 before this fix)
 *   majorToMinorUnits(1.25, 'BHD') -> 1250
 */
export function majorToMinorUnits(amountMajor: number, code: string | null | undefined): number {
  return Math.round(amountMajor * 10 ** currencyExponent(code));
}

/**
 * Convert an integer minor-unit amount to its major-unit value for display.
 * Mirrors backend minor_units_to_major.
 *
 *   minorUnitsToMajor(2500, 'USD') -> 25
 *   minorUnitsToMajor(2500, 'JPY') -> 2500
 *   minorUnitsToMajor(1250, 'BHD') -> 1.25
 */
export function minorUnitsToMajor(amountMinor: number, code: string | null | undefined): number {
  return amountMinor / 10 ** currencyExponent(code);
}
